import TomSelect from 'tom-select';

const MAP_ZOOM_MIN = 1;
const MAP_ZOOM_MAX = 3;
const MAP_ZOOM_STEP = 0.5;

function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function buildClientCard(client, showContent, labels) {
    const building = client.Building;
    const lines = [];

    if (client.Zusatzname) lines.push(escapeHtml(client.Zusatzname));
    if (building) {
        lines.push(escapeHtml(building.Name) + (client.Ebene ? `, ${escapeHtml(client.Ebene)}` : ''));
        if (building.Adresse) lines.push(`${escapeHtml(building.Adresse)}<br/>${escapeHtml(building.PLZ)} ${escapeHtml(building.Ort)}`);
    }
    if (client.Telefon) lines.push(`T ${escapeHtml(client.Telefon)}`);
    if (client.Fax) lines.push(`F ${escapeHtml(client.Fax)}`);
    if (client.Email) lines.push(`E <a href="mailto:${escapeHtml(client.Email)}">${escapeHtml(client.Email)}</a>`);
    if (client.Link) lines.push(`<a href="${escapeHtml(client.Link)}" target="_blank" rel="noopener">${escapeHtml(client.Link)}</a>`);

    let postAddress = '';
    if (client.HasDifferentAddress) {
        postAddress = `<br/><em>${escapeHtml(labels.postaddress)}:</em><br/>${escapeHtml(client.Adresse)}<br/>${escapeHtml(client.PLZ)} ${escapeHtml(client.Ort)}`;
    }

    const competences = client.Expertises && client.Expertises.length
        ? `<div class="client_content_competences">
            <p><em>${escapeHtml(labels.competences)}:</em></p>
            <ul>${client.Expertises.map((name) => `<li>${escapeHtml(name)}</li>`).join('')}</ul>
        </div>`
        : '';

    const mapUrl = client.MapURL || '';

    return `<div class="client${showContent ? ' client--showcontent' : ''}" data-map="${escapeHtml(mapUrl)}">
        <div class="client_head">
            <h2>${escapeHtml(client.Name)}</h2>
            <p>${escapeHtml(client.FormatSectors)}</p>
        </div>
        <div class="client_content">
            <div class="client_content_text">
                <p>${lines.join('<br/>')}${postAddress}</p>
            </div>
            ${competences}
            <div class="client_content_map">
                <img src="${escapeHtml(mapUrl)}" alt=""/>
            </div>
        </div>
    </div>`;
}

function paramsFromForm(form) {
    const data = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of data.entries()) {
        if (value !== '' && value !== null) params.set(key, value);
    }
    return params;
}

function applyParamsToForm(form, params) {
    form.querySelectorAll('input[name], select[name]').forEach((field) => {
        field.value = params.get(field.name) ?? '';
    });
}

export function initClientSearch() {
    const section = document.querySelector('[data-behaviour="client-search"]');
    if (!section) return;

    const form = section.querySelector('[data-behaviour="client-search-form"]');
    const resultsEl = section.querySelector('[data-behaviour="client-results"]');
    const mapImg = section.querySelector('#client-map');
    const searchUrl = section.dataset.searchUrl;
    const labels = {
        competences: resultsEl.dataset.competencesLabel,
        postaddress: resultsEl.dataset.postaddressLabel,
    };
    const emptyText = resultsEl.dataset.emptyText;

    const searchableSelects = Array.from(form.querySelectorAll('[data-behaviour="client-searchable-select"]'))
        .map((select) => new TomSelect(select, {
            maxOptions: null,
        }));

    const mapState = { zoom: 1, panX: 0, panY: 0 };

    function applyMapTransform() {
        mapImg.style.transform = `translate(${mapState.panX}px, ${mapState.panY}px) scale(${mapState.zoom})`;
    }

    function setActiveMap(src) {
        if (!src) return;
        mapImg.src = src;
        mapState.zoom = 1;
        mapState.panX = 0;
        mapState.panY = 0;
        applyMapTransform();
    }

    function resetMap() {
        setActiveMap(mapImg.dataset.default);
    }

    function renderResults(clients) {
        if (!clients.length) {
            resultsEl.innerHTML = `<p class="clients-empty">${escapeHtml(emptyText)}</p>`;
            resetMap();
            return;
        }

        const showContent = clients.length === 1;
        resultsEl.innerHTML = clients
            .map((client) => buildClientCard(client, showContent, labels))
            .join('');

        if (showContent) {
            setActiveMap(clients[0].MapURL);
        } else {
            resetMap();
        }

        resultsEl.querySelectorAll('.client').forEach((card) => {
            card.querySelector('.client_head').addEventListener('click', () => {
                const isOpen = card.classList.toggle('client--showcontent');
                if (isOpen) {
                    setActiveMap(card.dataset.map);
                } else {
                    resetMap();
                }
            });
        });
    }

    async function fetchClients(params) {
        resultsEl.innerHTML = '<p class="clients-loading">…</p>';
        const query = params.toString();
        const response = await fetch(searchUrl + (query ? `?${query}` : ''));
        const clients = await response.json();
        renderResults(clients);
    }

    function updateFromForm(pushHistory) {
        const params = paramsFromForm(form);
        if (pushHistory) {
            const url = params.toString() ? `?${params.toString()}` : window.location.pathname;
            window.history.replaceState(null, '', url);
        }
        fetchClients(params);
    }

    form.addEventListener('submit', (event) => {
        event.preventDefault();
        updateFromForm(true);
    });

    searchableSelects.forEach((tomselect) => {
        tomselect.on('change', () => updateFromForm(true));
    });

    const resetLink = form.querySelector('[data-behaviour="client-search-reset"]');
    if (resetLink) {
        resetLink.addEventListener('click', () => {
            form.reset();
            searchableSelects.forEach((tomselect) => tomselect.clear());
            updateFromForm(true);
        });
    }

    const detailToggle = section.querySelector('#toggle-detail-form');
    if (detailToggle) {
        detailToggle.addEventListener('click', () => {
            form.querySelector('.section_filters').classList.toggle('section_filters--show');
        });
    }

    section.querySelectorAll('.zoom-in, .zoom-out').forEach((button) => {
        button.addEventListener('click', () => {
            mapState.zoom = button.classList.contains('zoom-in')
                ? Math.min(MAP_ZOOM_MAX, mapState.zoom + MAP_ZOOM_STEP)
                : Math.max(MAP_ZOOM_MIN, mapState.zoom - MAP_ZOOM_STEP);
            applyMapTransform();
        });
    });

    const mapContainer = mapImg.closest('.section_map_container');
    if (mapContainer) {
        let dragging = false;
        let startX = 0;
        let startY = 0;
        let startPanX = 0;
        let startPanY = 0;

        mapImg.addEventListener('pointerdown', (event) => {
            dragging = true;
            startX = event.clientX;
            startY = event.clientY;
            startPanX = mapState.panX;
            startPanY = mapState.panY;
            mapImg.setPointerCapture(event.pointerId);
            mapContainer.classList.add('section_map_container--dragging');
        });

        mapImg.addEventListener('pointermove', (event) => {
            if (!dragging) return;
            mapState.panX = startPanX + (event.clientX - startX);
            mapState.panY = startPanY + (event.clientY - startY);
            applyMapTransform();
        });

        const stopDragging = (event) => {
            if (!dragging) return;
            dragging = false;
            mapContainer.classList.remove('section_map_container--dragging');
            if (mapImg.hasPointerCapture(event.pointerId)) {
                mapImg.releasePointerCapture(event.pointerId);
            }
        };

        mapImg.addEventListener('pointerup', stopDragging);
        mapImg.addEventListener('pointercancel', stopDragging);
    }

    const initialParams = new URLSearchParams(window.location.search);
    applyParamsToForm(form, initialParams);
    searchableSelects.forEach((tomselect) => {
        const value = initialParams.get(tomselect.input.name);
        if (value) tomselect.setValue(value);
    });
    fetchClients(initialParams);
}
